import type { SpinResponse } from "@slot/client-engine";
import type { GameHistoryData, GameHistoryEntry, ScriptedFixtureCatalog } from "@slot/game-shell";
import { LINE_OPTIONS } from "../rules/lines";
import { EMPTY_POTS } from "../rules/symbols";
import { FIXTURE_SCRIPTS } from "./fixtureScripts";
import { buildRound } from "./RoundBuilder";
import { scriptedRound } from "./scriptedRound";

/**
 * The history's rounds and the offline wallet (the shell's offline settings: a line's bet of 1.00, 10,000.00 EUR): the
 * rounds were played on every line, so the history's bet is their total (decision 56) and every script plays its bonus.
 */
const HISTORY_LINES = Math.max(...LINE_OPTIONS);
const HISTORY_BET_MINOR = String(100 * HISTORY_LINES);
export const OFFLINE_BALANCE_MINOR = "1000000";
export const OFFLINE_CURRENCY = "EUR";

/** A scripted round on the wire, played on every line at a line's offline bet, under `roundIdentifier`. */
export function scriptedResponse(fixtureId: string, roundIdentifier: string): SpinResponse {
  const fixture = FIXTURE_SCRIPTS.find((f) => f.id === fixtureId);
  if (!fixture) throw new Error(`scriptedResponse: no scripted round "${fixtureId}"`);
  const outcome = scriptedRound(HISTORY_BET_MINOR, HISTORY_LINES, fixture.script, { pots: EMPTY_POTS, lastLineBetMinor: null });
  return buildRound({ roundIdentifier, outcome, currencyCode: OFFLINE_CURRENCY, balanceMinor: OFFLINE_BALANCE_MINOR });
}

/** The history's rounds the offline replay plays: the round id → its scripted round. */
const REPLAYABLE: readonly { roundId: string; fixtureId: string; date: string }[] = [
  { roundId: "5300000000004", fixtureId: "bonus_grow_royal", date: "2026-10-05 14:33" },
  { roundId: "5300000000003", fixtureId: "bonus_collect", date: "2026-10-05 14:32" },
  { roundId: "5300000000002", fixtureId: "base_coins", date: "2026-10-05 14:31" },
];

/** `?replay=` / `?pending=` offline: the history's rounds by id, the collect bonus for any other id (the shell's `fail` aside). */
export function createReplayRounds(): { roundsById: Record<string, () => SpinResponse>; defaultRound: () => SpinResponse } {
  return {
    roundsById: Object.fromEntries(REPLAYABLE.map((r) => [r.roundId, () => scriptedResponse(r.fixtureId, r.roundId)])),
    defaultRound: () => scriptedResponse("bonus_collect", "5300000000001"),
  };
}

/** The offline history: the replayable rounds, newest first (the first a failed one), and the two leaderboards. */
export function createHistoryFixture(): GameHistoryData {
  const row = (roundId: string, date: string, winAmountMinor: string, status: "completed" | "failed"): GameHistoryEntry => ({
    gameName: "coin-hold",
    date,
    betAmountMinor: HISTORY_BET_MINOR,
    winAmountMinor,
    roundId,
    replayUrl: null,
    currencyCode: OFFLINE_CURRENCY,
    status,
    chargeAmountMinor: HISTORY_BET_MINOR,
    costMultiplier: 1,
  });
  const rows = REPLAYABLE.map((r) => {
    const response = scriptedResponse(r.fixtureId, r.roundId);
    const win = response.roundSnapshot.orderedEvents[response.roundSnapshot.orderedEvents.length - 1]!.accumulatedWinAmountAfterEvent ?? "0";
    return row(r.roundId, r.date, win, "completed");
  });
  const best = [...rows].sort((a, b) => Number(BigInt(b.winAmountMinor) - BigInt(a.winAmountMinor)));
  return {
    betHistory: [row("5300000000005", "2026-10-05 14:34", "0", "failed"), ...rows],
    biggestWinsAllGames: best.slice(0, 1),
    biggestWinsCurrentGame: best.slice(0, 2),
  };
}

/** The shell's catalog shape (the definition requires one; this title's offline server plays its own rounds). */
export function createFixtureCatalog(): ScriptedFixtureCatalog {
  return {
    baseFixtures: FIXTURE_SCRIPTS.filter((f) => f.group === "Base").map((f, i) => scriptedResponse(f.id, `catalog-base-${i}`)),
    featureFixtures: FIXTURE_SCRIPTS.filter((f) => f.group !== "Base").map((f, i) => scriptedResponse(f.id, `catalog-feature-${i}`)),
  };
}
